import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/lib/utils';
import { getOrganizations, createOrganization } from '../api/organizations-api';
import type { Organization } from '@/types/api';

const PER_PAGE = 15;

export function useOrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (debouncedSearch === (searchParams.get('q') || '')) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (debouncedSearch) next.set('q', debouncedSearch); else next.delete('q');
      next.delete('page');
      return next;
    }, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadOrganizations();
  }, [page, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadOrganizations() {
    setLoading(true);
    try {
      const response = await getOrganizations({ search: debouncedSearch || undefined, page, perPage: PER_PAGE });
      setOrganizations(response.items);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setLoading(false);
    }
  }

  function handlePageChange(newPage: number) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newPage > 1) next.set('page', String(newPage)); else next.delete('page');
      return next;
    }, { replace: true });
  }

  async function handleCreateOrganization(name: string, slug: string) {
    await createOrganization({ name, slug });
    setIsCreateModalOpen(false);
    await loadOrganizations();
  }

  return {
    organizations,
    loading,
    totalCount,
    totalPages,
    page,
    searchInput,
    setSearchInput,
    handlePageChange,
    isCreateModalOpen,
    setIsCreateModalOpen,
    handleCreateOrganization,
  };
}
